// RGR11 요청·응답 타입 + 화면 모델 변환. 계획서 3절(`docs/plan/LOGISPRD-12104-rgr11.md`) 계약을 옮긴다.
import dayjs from 'dayjs'

/** `GET /api/v1/return/work-invoices` 조회 파라미터(3.1). */
export interface WorkInvoiceQuery {
  shipper: string
  rtnInvoiceNo?: string
  orgInvoiceNo?: string
  actDateFrom: string
  actDateTo: string
  centerNm?: string
  userId: string
}

/** `ReturnWorkInvoiceDto` — 18개 원본 필드 + 후처리 3개(첫 행에만 실린다). */
export interface WorkInvoiceRes {
  sprNm: string | null
  rtnInvoiceNo: string | null
  orgInvoiceNo: string | null
  claimType: string | null
  customerClaim: string | null
  centerClaim: string | null
  receiptDate: string | null
  ordRemark: string | null
  giReportId: number | null
  trsInvoiceNo: string | null
  ordNm: string | null
  trsNm: string | null
  trsPhone: string | null
  trsZip: string | null
  trsAddr: string | null
  trsMobile: string | null
  ordConsolNo: string | null
  refNo2: string | null
  /** 첫 행에만 실린다. */
  ordNoMax?: number | null
  giIds?: number[]
  packageType?: string | null
}

/** `GET /api/v1/return/work-items` 조회 파라미터(3.2). `giId` 는 반복 파라미터다. */
export interface WorkItemQuery {
  shipper?: string
  flag?: boolean
  barcode?: string
  rtnInvoiceNo?: string
  orgInvoiceNo?: string
  actDateFrom?: string
  actDateTo?: string
  giId?: number[]
}

/** `ReturnWorkItemDto` — 3분기 합집합. 분기 밖 필드는 null 이다. */
export interface WorkItemRes {
  sprNm: string | null
  nowDate: string | null
  returnId: number | null
  claimId: string | null
  whNm: string | null
  ordQty: number | null
  rtnInvoiceNo: string | null
  orgInvoiceNo: string | null
  claimType: string | null
  customerClaim: string | null
  centerClaim: string | null
  receiptDate: string | null
  ordRemark: string | null
  refNo1: string | null
  refNo2: string | null
  barcode: string | null
  skuId: string | null
  giWhNm: string | null
  gdsCd: string | null
  gdsNm: string | null
  gdsOpt: string | null
  salChannel: string | null
  brdNm: string | null
  giId: number | null
  stlNo: string | null
  gdsGroup1: string | null
  gdsColor: string | null
  gdsAttr1: string | null
}

/** 검수표 행 — 생성 셸 `COLUMNS`(`V2Page.generated.tsx`) dataIndex 와 이름을 맞춘다. */
export interface WorkTableRow {
  key: string
  /** 1.0 RGR11_ITEM order 2의 읽기 전용 화주코드. */
  SPR_NM: string
  /** 반품번호 — 채번은 클라이언트가 한다(6절 4단계). */
  ORD_NO: string
  /**
   * 반품처 편집 셀(LOGISPRD-12384 BE 조사 결론). `ReturnWorkItemDto` 에 없는 이유는 DTO 누락이
   * 아니라 애초에 작업자가 채우는 값이기 때문이다(1.0 ITEM 스캔 쿼리에도 없다). 저장 계약
   * (`SaveItemReq`)에도 이 필드가 없어 편집해도 저장 요청에는 실리지 않는다 — 저장하려면
   * INSERT 컬럼 추가가 필요한 별도 티켓 대상이다. 콤보 옵션은 `feature/LOGISPRD-12385-
   * combo-options-api` 병행 작업이라 이 화면은 자유입력으로만 연다.
   */
  RL_CHANNEL: string
  BARCODE: string
  SKU_ID: string
  GDS_CD: string
  GDS_NM: string
  GDS_OPT: string
  ORD_QTY: number | null
  /** 정불구분. 편집 셀(select). 공백이면 서버가 "정상" 으로 채운다(3.3). */
  ETC5: string
  /** 비고. 편집 셀(input) — 작업자 입력값이다(LOGISPRD-12384). */
  PROC_NM: string
  REF_NO1: string
  /** 원일련번호 — 화면에 표시하지 않는다. 저장 요청 `referenceNumber2` 매핑용(3.3). */
  REF_NO2: string
  SAL_CHANNEL: string
  BRD_NM: string
  /** 반품택배 편집 셀 — 작업자 입력값이다. 저장 계약이 `logisticsProviderName` 으로 받는다. */
  TRS_LSP_NM: string
  /** 반품계좌 편집 셀 — 작업자 입력값이다(1.0 은 그리드 메타 preset). 저장 계약이 `etc1` 로 받는다. */
  ETC1: string
  /** 반송금액 편집 셀. 저장 계약(`etc2`)이 문자열이라 문자열로 들고 있다가 그대로 보낸다. */
  ETC2: string
  /** 착불금액 편집 셀. 저장 계약(`etc3`)이 문자열이라 문자열로 들고 있다가 그대로 보낸다. */
  ETC3: string
  ETC4: string
  // 생성일자·생성자·수정일자·수정자 — 서버가 채운다(생성일시는 주입 Clock KST, 생성자는 인증
  // principal email). 요청에 실어도 서버가 무시하므로 저장 전 행에는 값이 없는 것이 맞다.
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
  /** 저장 요청 조립용 — 화면에 표시하지 않는다. */
  returnId: number | null
  giId: number | null
  claimId: string | null
  stlNo: string | null
  gdsGroup1: string | null
  gdsColor: string | null
  gdsAttr1: string | null
}

/** HEAD 조회 결과를 화면 헤더 표(`core.Descriptions`)에 그대로 옮긴 모델. */
export interface WorkInvoiceHead {
  sprNm: string
  rtnInvoiceNo: string
  orgInvoiceNo: string
  claimType: string
  customerClaim: string
  centerClaim: string
  receiptDate: string
  ordRemark: string
  giReportId: number | null
  trsInvoiceNo: string
  ordNm: string
  trsNm: string
  trsPhone: string
  trsZip: string
  trsAddr: string
  trsMobile: string
  ordConsolNo: string
  refNo2: string
}

/** HEAD 응답 첫 행에서만 읽는 후처리 값. */
export interface WorkInvoiceMeta {
  ordNoMax: number | null
  giIds: number[]
  packageType: string | null
}

/** `POST /api/v1/return/work-registrations/save` 요청 head(3.3). */
export interface SaveHeadReq {
  shipperCode: string
  returnInvoiceNumber: string
  originalInvoiceNumber?: string
  receiverAddress?: string
  receiverPhone?: string
  receiverName?: string
  /** `\d{14}`(yyyyMMddHHmmss) — 스캔 시작 시각. */
  receiptStartedAt: string
  jobCommentForInvoice?: string
  isSendToDoosonERP?: boolean
}

/** 저장 요청 item(3.3). 필수는 `orderNumber`·`orderQuantity` 뿐이다. */
export interface SaveItemReq {
  returnId?: number
  giId?: number
  orderNumber: string
  orderOptionNumber?: string
  plannedDate?: string
  barcode?: string
  uid?: string
  skuId?: string
  optionCode?: number
  goodsCode?: string
  goodsName?: string
  goodsOption?: string
  goodsColor?: string
  goodsGroup1?: string
  goodsAttr1?: string
  orderQuantity: number
  condition?: string
  procurementName?: string
  etc1?: string
  etc2?: string
  etc3?: string
  etc4?: string
  salesChannel?: string
  brandName?: string
  styleNumber?: string
  referenceNumber1?: string
  referenceNumber2?: string
  referenceNumber5?: string
  logisticsProviderName?: string
  transferType?: string
  transferNumber?: string
  transferMessage?: string
  warehouseName?: string
  warehouseArea?: string
  orderOptionSequence?: number
  purchasePrice?: number
  lotAttribute5?: string
  claimId?: string
  claimType?: string
  incoterms?: string
  orderStatus?: string
}

export interface SaveReq {
  head: SaveHeadReq
  items: SaveItemReq[]
}

export interface SaveRes {
  inserted: { id: number; asnSequence: number; orderNumber: string; orderOptionNumber: string | null }[]
  duplicates: { orderNumber: string; existingTargetIds: number[] }[]
  updatedReturnIds: number[]
  attachmentTargetIds: number[]
}

/** `GET /api/v1/return/management/order-search` 조회 파라미터(3.4). */
export interface OrderSearchQuery {
  shipper?: string
  barcode?: string[]
  refNo1?: string[]
  trsInvoiceNo?: string
  ordNm?: string
  trsNm?: string
  trsPhone?: string
  trsAddr?: string
  actDateFrom: string
  actDateTo: string
}

/** `ReturnOrderSearchDto` — `refNo3 == trsInvoiceNo`, `ordQty == actQty` alias. */
export interface OrderSearchRes {
  sprNm: string | null
  actDate: string | null
  ordNm: string | null
  trsNm: string | null
  trsMobile: string | null
  trsPhone: string | null
  trsZip: string | null
  trsAddr: string | null
  barcode: string | null
  actQty: number | null
  refNo1: string | null
  salChannel: string | null
  brdNm: string | null
  gdsCd: string | null
  gdsNm: string | null
  gdsOpt: string | null
  trsInvoiceNo: string | null
  refNo3: string | null
  ordQty: number | null
}

/** 주문검색표 행 — 생성 셸 `COLUMNS2` dataIndex 와 이름을 맞춘다. */
export interface OrderSearchRow {
  key: string
  SPR_NM: string
  GI_DATE: string
  TRS_INVOICE_NO: string
  ORD_NM: string
  TRS_NM: string
  TRS_PHONE: string
  TRS_TEL: string
  TRS_ZIP: string
  TRS_ADDR: string
  BARCODE: string
  ORD_QTY: number | null
  REF_NO1: string
  SAL_CHANNEL: string
  BRD_NM: string
  /** 원송장 채우기용 — 표에는 없지만 선택 시 필요하다. */
  ORG_INVOICE_NO: string
}

/** `POST /api/v1/common/file/uploads` — 키·presigned PUT 발급(3.5 1단계). */
export interface FileUploadPrepareReq {
  shipper: string
  fileName: string
  contentType: string
}

export interface FileUploadPrepareRes {
  key: string
  uploadUrl: string
  signedHeaders: Record<string, string[]>
  expiresInSeconds: number
}

/** `PATCH /api/v1/common/file/uploads` — 첨부 연결(3.5 3단계). */
export type ResourceType = 'ATCH_FILE' | 'VIDEO'

export interface FileUploadCompleteReq {
  shipper: string
  key: string
  resourceType: ResourceType
  targetIds: number[]
}

export interface FileUploadCompleteRes {
  fileInfoId: number
  key: string
  linkedTargetCount: number
}

const toStr = (v: string | null | undefined): string => v ?? ''

/** HEAD 응답 첫 행 → 헤더 표 모델. */
export const toWorkInvoiceHead = (res: WorkInvoiceRes): WorkInvoiceHead => ({
  sprNm: toStr(res.sprNm),
  rtnInvoiceNo: toStr(res.rtnInvoiceNo),
  orgInvoiceNo: toStr(res.orgInvoiceNo),
  claimType: toStr(res.claimType),
  customerClaim: toStr(res.customerClaim),
  centerClaim: toStr(res.centerClaim),
  receiptDate: toStr(res.receiptDate),
  ordRemark: toStr(res.ordRemark),
  giReportId: res.giReportId,
  trsInvoiceNo: toStr(res.trsInvoiceNo),
  ordNm: toStr(res.ordNm),
  trsNm: toStr(res.trsNm),
  trsPhone: toStr(res.trsPhone),
  trsZip: toStr(res.trsZip),
  trsAddr: toStr(res.trsAddr),
  trsMobile: toStr(res.trsMobile),
  ordConsolNo: toStr(res.ordConsolNo),
  refNo2: toStr(res.refNo2),
})

/** HEAD 응답 첫 행에서만 읽는다(2건 이상이면 2번째 행부터 세 필드가 없다 — 3.1). */
export const toWorkInvoiceMeta = (rows: WorkInvoiceRes[]): WorkInvoiceMeta => ({
  ordNoMax: rows[0]?.ordNoMax ?? null,
  giIds: rows[0]?.giIds ?? [],
  packageType: rows[0]?.packageType ?? null,
})

/** ITEM 응답 1건 → 표 행. `ordNo` 는 호출부(채번 로직)가 정한다. */
export const toWorkTableRow = (res: WorkItemRes, ordNo: string, key: string): WorkTableRow => ({
  key,
  SPR_NM: toStr(res.sprNm),
  ORD_NO: ordNo,
  RL_CHANNEL: '',
  BARCODE: toStr(res.barcode),
  SKU_ID: toStr(res.skuId),
  GDS_CD: toStr(res.gdsCd),
  GDS_NM: toStr(res.gdsNm),
  GDS_OPT: toStr(res.gdsOpt),
  ORD_QTY: res.ordQty,
  ETC5: '정상',
  PROC_NM: '',
  REF_NO1: toStr(res.refNo1),
  REF_NO2: toStr(res.refNo2),
  SAL_CHANNEL: toStr(res.salChannel),
  BRD_NM: toStr(res.brdNm),
  TRS_LSP_NM: '',
  ETC1: '',
  ETC2: '',
  ETC3: '',
  ETC4: '',
  INS_DATE: '',
  INS_BY: '',
  UPD_DATE: '',
  UPD_BY: '',
  returnId: res.returnId,
  giId: res.giId,
  claimId: res.claimId,
  stlNo: res.stlNo,
  gdsGroup1: res.gdsGroup1,
  gdsColor: res.gdsColor,
  gdsAttr1: res.gdsAttr1,
})

/**
 * 표 행 → 저장 item. `returnId`·`giId` 는 둘 다 있을 때만 원출고 보정이 걸린다(3.3) —
 * 한쪽만 있으면 보정 없이 요청 값이 그대로 저장되므로 값을 지어내 짝을 맞추지 않는다.
 */
export const toSaveItem = (row: WorkTableRow): SaveItemReq => ({
  returnId: row.returnId ?? undefined,
  giId: row.giId ?? undefined,
  orderNumber: row.ORD_NO,
  barcode: row.BARCODE || undefined,
  goodsCode: row.GDS_CD || undefined,
  goodsName: row.GDS_NM || undefined,
  goodsOption: row.GDS_OPT || undefined,
  goodsColor: row.gdsColor ?? undefined,
  goodsGroup1: row.gdsGroup1 ?? undefined,
  goodsAttr1: row.gdsAttr1 ?? undefined,
  orderQuantity: row.ORD_QTY ?? 0,
  // 공백이면 필드 자체를 빼 서버가 "정상" 으로 채우게 한다(3.3, 7절 테스트: 정불구분 미입력 시 condition 미전송).
  condition: row.ETC5 || undefined,
  procurementName: row.PROC_NM || undefined,
  salesChannel: row.SAL_CHANNEL || undefined,
  brandName: row.BRD_NM || undefined,
  styleNumber: row.stlNo ?? undefined,
  referenceNumber1: row.REF_NO1 || undefined,
  referenceNumber2: row.REF_NO2 || undefined,
  // TRS_LSP_NM·ETC1~4 는 편집 셀 값을 그대로 싣는다(LOGISPRD-12384) — RL_CHANNEL 과 달리
  // 저장 계약(SaveItemReq)에 필드가 있어 작업자가 입력하면 저장 요청에 그대로 실려 나간다.
  logisticsProviderName: row.TRS_LSP_NM || undefined,
  etc1: row.ETC1 || undefined,
  etc2: row.ETC2 || undefined,
  etc3: row.ETC3 || undefined,
  etc4: row.ETC4 || undefined,
  claimId: row.claimId ?? undefined,
})

/** `\d{8}`(yyyyMMdd). */
export const toYyyyMmDd = (date: Date): string => dayjs(date).format('YYYYMMDD')

/** `\d{14}`(yyyyMMddHHmmss) — 저장 head `receiptStartedAt`(1.0 `REC_START_DATE`, strict). */
export const toYyyyMmDdHHmmss = (date: Date): string => dayjs(date).format('YYYYMMDDHHmmss')

export interface BuildSaveRequestParams {
  /** 로그인 사용자 화주. HEAD 응답 `sprNm` 을 실으면 안 된다(3.3 — 통합반품 등에서 403 이 난다). */
  shipperCode: string
  returnInvoiceNumber: string
  originalInvoiceNumber?: string
  receiverAddress?: string
  receiverPhone?: string
  receiverName?: string
  /** 스캔 시작 시각(1.0 `REC_START_DATE`) — 호출부가 `toYyyyMmDdHHmmss` 로 만들어 넘긴다. */
  receiptStartedAt: string
  jobCommentForInvoice?: string
  isSendToDoosonERP?: boolean
  rows: WorkTableRow[]
}

/** 표 행 전체 + 헤더 입력값 → 저장 요청. */
export const buildSaveRequest = (params: BuildSaveRequestParams): SaveReq => ({
  head: {
    shipperCode: params.shipperCode,
    returnInvoiceNumber: params.returnInvoiceNumber,
    originalInvoiceNumber: params.originalInvoiceNumber || undefined,
    receiverAddress: params.receiverAddress || undefined,
    receiverPhone: params.receiverPhone || undefined,
    receiverName: params.receiverName || undefined,
    receiptStartedAt: params.receiptStartedAt,
    jobCommentForInvoice: params.jobCommentForInvoice || undefined,
    isSendToDoosonERP: params.isSendToDoosonERP ?? false,
  },
  items: params.rows.map(toSaveItem),
})

/** 주문검색 응답 → 표 행. `refNo3==trsInvoiceNo`, `ordQty==actQty` 는 alias 라 값이 같다(3.4). */
export const toOrderSearchRow = (res: OrderSearchRes, key: string): OrderSearchRow => ({
  key,
  SPR_NM: toStr(res.sprNm),
  GI_DATE: toStr(res.actDate),
  TRS_INVOICE_NO: toStr(res.trsInvoiceNo),
  ORD_NM: toStr(res.ordNm),
  TRS_NM: toStr(res.trsNm),
  TRS_PHONE: toStr(res.trsPhone),
  TRS_TEL: toStr(res.trsMobile),
  TRS_ZIP: toStr(res.trsZip),
  TRS_ADDR: toStr(res.trsAddr),
  BARCODE: toStr(res.barcode),
  ORD_QTY: res.ordQty ?? res.actQty,
  REF_NO1: toStr(res.refNo1),
  SAL_CHANNEL: toStr(res.salChannel),
  BRD_NM: toStr(res.brdNm),
  ORG_INVOICE_NO: toStr(res.trsInvoiceNo ?? res.refNo3),
})
